import { App } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../api/client';
import { shipmentsApi } from '../api/endpoints';
import { triggerBlobDownload } from '../lib/download';

interface DownloadArgs {
  id: string;
  view: 'store' | 'sklad';
  /** Document number, used for the fallback filename. */
  msName: string;
  /** Narrows a multi-sklad doc to one sklad's lines (sklad view). */
  skladId?: string;
}

/**
 * Downloads a накладная PDF through the authenticated `api` instance and saves
 * it to the device. Tracks a pending flag for button spinners and surfaces
 * errors as a toast (a SELLER asking for the sklad copy gets a 403 here).
 */
export function useDownloadShipmentPdf(): {
  download: (args: DownloadArgs) => Promise<void>;
  isPending: boolean;
} {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [isPending, setPending] = useState(false);

  const download = async ({ id, view, msName, skladId }: DownloadArgs): Promise<void> => {
    setPending(true);
    try {
      const { blob, filename } = await shipmentsApi.downloadPdf(id, view, skladId);
      const suffix = view === 'sklad' ? 'sklad' : 'dokon';
      triggerBlobDownload(blob, filename ?? `Nakladnaya-${msName}-${suffix}.pdf`);
    } catch (error) {
      message.error(apiErrorMessage(error) ?? t('common.requestError'));
    } finally {
      setPending(false);
    }
  };

  return { download, isPending };
}
